import { ILoginFormValues, KeycloakUserResponses } from '@common/types/user';
import { useLocaleFormRevalidation } from '@hooks/useLocaleFormRevalidation';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    Alert,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    InputLabel,
    Link as MUILink,
    TextField,
    Typography,
} from '@mui/material';
import { createLoginValidationSchema } from '@schemas/Authentication/LoginSchema';
import { useLoginMutation, useMagicLinkMutation, useReactivationRequestMutation } from '@services/accounts';
import { useLazyCheckEmailQuery, useLazyGetKeycloakLoginUrlQuery } from '@services/keycloak';
import { handleError, toaster } from '@utils/common';
import { cookies } from '@utils/cookies';
import { pathToVP } from '@utils/route';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';

enum ERROR_PARAMS {
    emailDoesNotExistInProf = 'email-does-not-exist-in-professional',
    userCanNotLoginWithIdp = 'user-can-not-login-with-idp',
}

const LoginForm = () => {
    const { formatMessage, locale } = useIntl();
    const LoginSchema = useMemo(() => createLoginValidationSchema(formatMessage), [formatMessage]);
    const [searchParams, setSearchParams] = useSearchParams();

    const errorParam = useMemo(() => {
        const emailDoesNotExist = searchParams.get(ERROR_PARAMS.emailDoesNotExistInProf);
        const userCanNotLogin = searchParams.get(ERROR_PARAMS.userCanNotLoginWithIdp);
        if (emailDoesNotExist) {
            return { param: ERROR_PARAMS.emailDoesNotExistInProf, value: emailDoesNotExist };
        }
        if (userCanNotLogin) {
            return { param: ERROR_PARAMS.userCanNotLoginWithIdp, value: userCanNotLogin };
        }
        return null;
    }, [searchParams]);

    const [showPassword, setShowPassword] = useState(false);
    const [checkStatus, setCheckStatus] = useState<KeycloakUserResponses | null>(null);
    const [errorAlert, setErrorAlert] = useState<
        | ERROR_PARAMS
        | KeycloakUserResponses.VERIFY_EMAIL
        | KeycloakUserResponses.NO_USER_FOUND_PROFESSIONAL
        | KeycloakUserResponses.DEACTIVATED
        | 'magic-link-fail'
        | 'unexpected'
        | null
    >(errorParam ? errorParam.param : null);

    const [checkEmail, { isLoading: isCheckingEmail }] = useLazyCheckEmailQuery();
    const [getKeycloakLoginUrl, { isLoading: isLoadingLoginUrl }] = useLazyGetKeycloakLoginUrlQuery();
    const [reactivationRequest, { isLoading: isReactivationRequestLoading }] = useReactivationRequestMutation();
    const [login] = useLoginMutation();
    const [emailMeMutation] = useMagicLinkMutation();
    const navigate = usePrefixedNavigate();

    // Formik setup
    const formik = useFormik<ILoginFormValues>({
        initialValues: {
            email: '',
            password: '',
            showPasswordField: false,
        },
        validationSchema: LoginSchema,
        onSubmit: async (values, { setErrors, setTouched }) => {
            setErrorAlert(null);
            try {
                if (!values.showPasswordField) {
                    // First step: check email with SSO endpoint
                    await handleEmailCheck(values.email);
                } else {
                    // Second step: submit login form
                    await handleLogin(values);
                }
            } catch (error: unknown) {
                if (error && typeof error === 'object' && 'inner' in error) {
                    // Yup validation errors
                    const validationErrors: Record<string, string> = {};
                    const touchedFields: Record<string, boolean> = {};

                    (error as { inner: Array<{ path?: string; message: string }> }).inner.forEach((err) => {
                        if (err.path) {
                            validationErrors[err.path] = err.message;
                            touchedFields[err.path] = true;
                        }
                    });

                    setErrors(validationErrors);
                    setTouched(touchedFields);
                }
            }
        },
    });

    useLocaleFormRevalidation(formik, locale);

    const showEmailMe = formik.values.showPasswordField && checkStatus === KeycloakUserResponses.LEGACY_LOGIN;

    const handleEmailMe = async () => {
        try {
            await emailMeMutation({ email: formik.values.email.toLowerCase() }).unwrap();
            toaster.success({
                msg: formatMessage({
                    id: 'auth.login.magicLinkSent',
                    defaultMessage: 'Magic link sent! Please check your email.',
                }),
            });
        } catch (error: unknown) {
            console.error('Magic link request failed:', error);
            setErrorAlert('magic-link-fail');
        }
    };

    const isSubmitDisabled = useMemo(
        () =>
            isCheckingEmail ||
            (!formik.values.showPasswordField && !formik.values.email) ||
            (formik.values.showPasswordField && (!formik.values.email || !formik.values.password)),
        [isCheckingEmail, formik.values]
    );

    // Check email with SSO endpoint
    const handleEmailCheck = async (email: string) => {
        try {
            email = email.toLowerCase();
            const { check_status } = await checkEmail(email).unwrap();
            setCheckStatus(check_status);

            if (check_status === KeycloakUserResponses.USES_KEYCLOAK) {
                // Redirect to Keycloak login
                getKeycloakLoginUrl({ email, locale })
                    .unwrap()
                    .then(({ login_url }) => {
                        window.location.href = login_url;
                    })
                    .catch(() => {
                        setErrorAlert('unexpected');
                    });
                return;
            }
            if (
                check_status === KeycloakUserResponses.NO_USER_FOUND_PROFESSIONAL ||
                check_status === KeycloakUserResponses.VERIFY_EMAIL ||
                check_status === KeycloakUserResponses.DEACTIVATED
            ) {
                setErrorAlert(check_status);
            } else {
                // Show password field for other responses
                formik.resetForm({ values: formik.values });
                formik.setFieldValue('showPasswordField', true);
            }
        } catch (error: unknown) {
            console.error(error);
            formik.setFieldError(
                'email',
                formatMessage({
                    id: 'common.errors.unexpected',
                    defaultMessage: 'Unexpected error occurred. Please try again later.',
                })
            );
        }
    };

    // Handle final login submission
    const handleLogin = async (values: ILoginFormValues) => {
        try {
            const userDetails = await login({ ...values, email: values.email.toLowerCase() }).unwrap();
            const nextParam = searchParams.get('next');
            const newParams = new URLSearchParams(searchParams);

            cookies.remove('keycloak_migration_required');

            if (checkStatus === KeycloakUserResponses.DOES_NOT_USE_KEYCLOAK) {
                navigate(
                    { pathname: '/auth/link-account', search: newParams.toString() },
                    { state: { userDetails: { ...userDetails, password: values.password } } }
                );
            } else if (checkStatus === KeycloakUserResponses.NO_USER_FOUND_KEYCLOAK) {
                navigate(
                    { pathname: '/auth/migrate-account', search: newParams.toString() },
                    { state: { userDetails: { ...userDetails, password: values.password } } }
                );
            } else if (checkStatus === KeycloakUserResponses.LEGACY_LOGIN) {
                if (nextParam) {
                    if (nextParam.startsWith('/app')) {
                        newParams.delete('next');
                        navigate({ pathname: nextParam, search: newParams.toString() });
                    } else {
                        window.location.href = nextParam;
                    }
                } else {
                    window.location.href = pathToVP().toString();
                }
            }
        } catch (error: unknown) {
            console.error('Login failed:', error);

            if (
                typeof error === 'object' &&
                error !== null &&
                'data' in error &&
                error.data &&
                typeof error.data === 'object' &&
                error.data !== null &&
                'non_field_errors' in error.data &&
                Array.isArray((error.data as { non_field_errors?: string[] }).non_field_errors)
            ) {
                formik.setFieldError('password', (error.data as { non_field_errors: string[] }).non_field_errors[0]);
            } else {
                formik.setFieldError(
                    'password',
                    formatMessage({
                        id: 'common.errors.unexpected',
                        defaultMessage: 'Unexpected error occurred. Please try again later.',
                    })
                );
            }
        }
    };

    const handleErrorAlertClose = () => {
        setErrorAlert(null);
        if (errorAlert === errorParam?.param) {
            searchParams.delete(errorParam?.param);
            setSearchParams(searchParams, { replace: true });
        }
    };

    const handleReactivationRequest = async () => {
        try {
            await reactivationRequest({ email: formik.values.email.toLowerCase() }).unwrap();
            toaster.success({
                msg: formatMessage({ id: 'auth.login.reactivationSent', defaultMessage: 'Reactivation request sent!' }),
            });
        } catch (error) {
            console.error('Reactivation request failed:', error);
            handleError(
                formatMessage({ id: 'auth.login.reactivationError', defaultMessage: 'Reactivation Request Error' }),
                error
            );
        }
    };

    const ErrorAlertContent = () => {
        if (!errorAlert) return null;

        return (
            <Typography fontFamily="Roboto" fontSize={16}>
                {(() => {
                    if (errorAlert === ERROR_PARAMS.emailDoesNotExistInProf) {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.emailDoesNotExistInProf"
                                defaultMessage="Your {oneIdBrand} {email} is not associated with a {professionalBrand} account. A {shortProfessionalBrand} account is needed to proceed with login."
                                description="Error message when user's email exists in OneID but not in Professional portal. Used in: /auth/login"
                                values={{
                                    oneIdBrand: 'Victron OneID',
                                    email: (
                                        <Typography component="span" fontFamily="Roboto" fontWeight={600}>
                                            ({errorParam?.value})
                                        </Typography>
                                    ),
                                    professionalBrand: 'Victron Professional',
                                    shortProfessionalBrand: 'Professional',
                                }}
                            />
                        );
                    } else if (errorAlert === ERROR_PARAMS.userCanNotLoginWithIdp) {
                        return (
                            <>
                                <Typography
                                    fontFamily="Roboto"
                                    fontSize={16}
                                    fontWeight={500}
                                    letterSpacing={0.15}
                                    mb={1}
                                >
                                    <FormattedMessage
                                        id="auth.login.errorAlert.socialLoginDisabled.title"
                                        defaultMessage="Unable to use social login providers"
                                        description="Error title when admin users cannot use social login. Used in: /auth/login"
                                    />
                                </Typography>
                                <FormattedMessage
                                    id="auth.login.errorAlert.socialLoginDisabled.description"
                                    defaultMessage="Due to your role, 2FA is required. Social login providers are disabled for administrative users."
                                    description="Error description explaining why admin users cannot use social login. Used in: /auth/login"
                                />
                            </>
                        );
                    } else if (errorAlert === 'unexpected') {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.unexpected"
                                defaultMessage="An unexpected error has occurred, please try again later or contact support."
                                description="Generic error message for unexpected login failures. Used in: /auth/login"
                            />
                        );
                    } else if (errorAlert === KeycloakUserResponses.VERIFY_EMAIL) {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.verifyEmail"
                                defaultMessage="Please verify your email address before logging in."
                                description="Error message when user must verify their email first. Used in: /auth/login"
                            />
                        );
                    } else if (errorAlert === KeycloakUserResponses.NO_USER_FOUND_PROFESSIONAL) {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.noUserFound"
                                defaultMessage="No professional account found for the provided email. Please check your credentials or sign up for a new account."
                                description="Error message when no Professional account exists for the email. Used in: /auth/login"
                            />
                        );
                    } else if (errorAlert === 'magic-link-fail') {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.magicLinkFail"
                                defaultMessage="Failed to send magic link. Please try again later."
                                description="Error message when magic link email fails to send. Used in: /auth/login"
                            />
                        );
                    } else if (errorAlert === KeycloakUserResponses.DEACTIVATED) {
                        return (
                            <FormattedMessage
                                id="auth.login.errorAlert.deactivated"
                                defaultMessage="Your account has been deactivated. If you want to reactivate your account, please {reactivateLink}."
                                description="Error message when account is deactivated with reactivation link. Used in: /auth/login"
                                values={{
                                    reactivateLink: (
                                        <MUILink
                                            component="button"
                                            type="button"
                                            disabled={isReactivationRequestLoading}
                                            onClick={handleReactivationRequest}
                                            className="inline-flex items-center gap-2"
                                            color={!isReactivationRequestLoading ? 'primary' : 'disabled'}
                                            underline={!isReactivationRequestLoading ? 'always' : 'none'}
                                        >
                                            <FormattedMessage
                                                id="auth.login.errorAlert.deactivated.clickHere"
                                                defaultMessage="click here"
                                                description="Link text for reactivating deactivated account. Used in: /auth/login"
                                            />
                                            {isReactivationRequestLoading ? <CircularProgress size={20} /> : ''}
                                        </MUILink>
                                    ),
                                }}
                            />
                        );
                    }
                })()}
            </Typography>
        );
    };

    const resetFlow = () => {
        setErrorAlert(null);
        setCheckStatus(null);
        setShowPassword(false);
        formik.resetForm();
    };

    return (
        <div className="w-full">
            <form onSubmit={formik.handleSubmit} noValidate>
                <Collapse in={!!errorAlert}>
                    <Alert severity="error" className="mb-6" onClose={handleErrorAlertClose}>
                        <ErrorAlertContent />
                    </Alert>
                </Collapse>
                <div className="mb-6">
                    <InputLabel htmlFor="email" className="block text-sm font-medium text-neutral-950 mb-2">
                        <FormattedMessage
                            id="common.fields.email.title"
                            defaultMessage="Email"
                            description="Label for email input field"
                        />
                    </InputLabel>
                    <TextField
                        type="email"
                        id="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        variant="outlined"
                        placeholder={formatMessage({
                            id: 'common.fields.email.placeholder',
                            defaultMessage: 'Your email address',
                        })}
                        disabled={isCheckingEmail || isLoadingLoginUrl || formik.values.showPasswordField}
                        error={Boolean(formik.touched.email && formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        size="small"
                        margin="none"
                        slotProps={{
                            input: {
                                className: ' rounded-lg',
                                endAdornment: formik.values.showPasswordField ? (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="reset form" onClick={resetFlow} edge="end">
                                            <RestartAltIcon color="black" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            },
                        }}
                    />
                </div>
                {formik.values.showPasswordField && (
                    <div className="flex flex-col items-end mb-6">
                        <div className="w-full">
                            <InputLabel htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                <FormattedMessage
                                    id="auth.fields.password.title"
                                    defaultMessage="Password"
                                    description="Label for password input field"
                                />
                            </InputLabel>
                            <TextField
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                                variant="outlined"
                                placeholder={formatMessage({
                                    id: 'auth.fields.password.placeholder',
                                    defaultMessage: 'Enter your password',
                                })}
                                error={Boolean(formik.touched.password && formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                size="small"
                                margin="none"
                                slotProps={{
                                    input: {
                                        className: 'rounded-lg',
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    edge="end"
                                                >
                                                    {!showPassword ? (
                                                        <VisibilityOff color="black" />
                                                    ) : (
                                                        <Visibility color="black" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                        <MUILink
                            component={Link}
                            to="../forgot-password"
                            state={{ login_hint: formik.values.email }}
                            underline="none"
                            align="right"
                            className="block mt-2 w-fit"
                            fontFamily="Open Sans"
                            fontSize={14}
                        >
                            <FormattedMessage
                                id="auth.login.forgotPassword"
                                defaultMessage="Forgot password?"
                                description="Link text for forgot password. Used in: /auth/login"
                            />
                        </MUILink>
                    </div>
                )}
                <Button
                    type="submit"
                    disabled={isSubmitDisabled}
                    fullWidth
                    className={clsx('rounded-full black-border', {
                        'mb-4': showEmailMe,
                    })}
                >
                    <span>
                        {isCheckingEmail
                            ? formatMessage({ id: 'auth.login.checking', defaultMessage: 'Checking...' })
                            : formik.values.showPasswordField
                            ? formatMessage({ id: 'auth.login.login', defaultMessage: 'Login' })
                            : formatMessage({ id: 'auth.login.continue', defaultMessage: 'Continue' })}
                    </span>
                </Button>
                {showEmailMe ? (
                    <Button
                        type="button"
                        disabled={isCheckingEmail || !formik.values.email}
                        fullWidth
                        className="rounded-full black-border mb-4"
                        color="white"
                        onClick={handleEmailMe}
                    >
                        <span>
                            <FormattedMessage
                                id="auth.login.emailLink"
                                defaultMessage="Email me a link to login"
                                description="Button text for requesting magic link login. Used in: /auth/login"
                            />
                        </span>
                    </Button>
                ) : null}
            </form>
        </div>
    );
};

export { LoginForm };
